import InputGroup from '@shared/components/InputGroup/InputGroup';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as UserPlus } from 'assets/icons/UserPlus.svg';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';

import AddedParticipantsList from './AddedParticipantsList/AddedParticipantsList';
import SuggestionDropdown from './SuggestionDropdown';
import useParticipantsField from './useParticipantsField';
import useCreateNewRoomDataContext from '../../CreateNewRoomDataProvider/useCreateNewRoomDataContext';

const MAX_PARTICIPANTS = 25;

const AddParticipantsField = () => {
  const { t } = useTranslation();
  const { mxLoggedIn } = useMxCookies();
  const { shouldDisplayError, handleChange, handleKeyDown, removeParticipant, addParticipant } = useParticipantsField();
  const {
    roomForm: {
      watch,
      control,
      formState: { errors },
    },
  } = useCreateNewRoomDataContext();

  const [inputWatch, participantsWatch] = watch(['participantInput', 'participants']);
  const inputError = errors.participantInput?.message;
  const errorToDisplay = shouldDisplayError ? errors.participantInput?.message : undefined;
  const shouldDisplaySuggestionDropdown = !!inputWatch && !inputError;
  const addedParticipantsCount = participantsWatch.length + (mxLoggedIn ? 1 : 0);
  const hasParticipantsToDisplay = !!addedParticipantsCount || mxLoggedIn;
  const isMaxParticipants = participantsWatch.length >= MAX_PARTICIPANTS;

  return (
    <div className="flex gap-5">
      <UserPlus className="stroke-gray-60 mt-3 h-6 w-6 shrink-0 stroke-1.5" />
      <div className="flex flex-col gap-4 flex-1 min-w-0">
        <div className="relative flex flex-col gap-2">
          <InputGroup variant="white-bg" errorMessage={errorToDisplay}>
            <Controller
              name="participantInput"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputGroup.Input
                  onChange={handleChange(onChange)}
                  value={value}
                  placeholder={t('add_participants')}
                  onKeyDown={handleKeyDown}
                  className="!bg-gray-10 disabled:!bg-gray-30"
                  disabled={isMaxParticipants}
                />
              )}
            />
          </InputGroup>
          {shouldDisplaySuggestionDropdown && <SuggestionDropdown onClick={addParticipant} />}
          <span className="text-size-sm text-black">{t('you_can_add_multiple_participants')}</span>
        </div>
        {hasParticipantsToDisplay && (
          <>
            <span className="text-size-xs text-black">
              {t('participants_count', { count: addedParticipantsCount, max: MAX_PARTICIPANTS })}
              {isMaxParticipants && <span className="text-alert-50 ml-1">({t('limit_reached').toLowerCase()})</span>}
            </span>
            <AddedParticipantsList onParticipantRemove={removeParticipant} />
          </>
        )}
      </div>
    </div>
  );
};

export default AddParticipantsField;
